import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { collapseCardStyles, cardContentStyles } from './commonStyles';
import i18n from 'i18n-js';
import { Card, CollapseCard } from 'components/UI';
import ItemStatus from 'components/ItemStatus';
import Loading from 'components/Loading';
import {
  CreateNFCCardModal,
  AssignNFCCardModal,
  AssignToUserNFCCardModal,
} from 'components/Modals';
import { AccessDeviceActions } from 'state/modules/accessDevice';
import { FETCH_STATE } from 'constants/common';
import { useConfirm } from 'hooks';

const StyledListItemText = styled(ListItemText)(() => ({
  '&.MuiListItemText-root': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  '& .MuiListItemText-primary': {
    display: 'inline-block',
    fontWeight: 'bold',
    fontSize: '0.875rem',
  },
  '& .MuiListItemText-secondary': {
    display: 'inline-block',
    fontSize: '0.875rem',
  },
}));

const NFCDetailsWidget = ({ id, user, view = 'nfc' }) => {
  const dispatch = useDispatch();
  const collapseCardClasses = collapseCardStyles();
  const cardContentClasses = cardContentStyles();
  const [openCreateNFCModal, setOpenCreateNFCModal] = useState(false);
  const [openAssignNFCModal, setOpenAssignNFCModal] = useState(false);
  const [openAssignToUserNFCModal, setOpenAssignToUserNFCModal] = useState(false);
  const {
    accessDevice,
    fetchingAccessDevice,
    updatingAccessDevice,
    assigningAccessDevice,
    unassigningAccessDevice,
  } = useSelector((state) => state.accessDevice);

  const loading = fetchingAccessDevice === FETCH_STATE.LOADING;
  const reload =
    updatingAccessDevice === FETCH_STATE.DONE ||
    assigningAccessDevice === FETCH_STATE.DONE ||
    unassigningAccessDevice === FETCH_STATE.DONE;

  useEffect(() => {
    dispatch(AccessDeviceActions.resetAccessDeviceState());
  }, [dispatch]);

  useEffect(() => {
    if (id && accessDevice?.id !== id) {
      dispatch(AccessDeviceActions.fetchAccessDeviceById(id));
    } else if (user?.id && accessDevice?.user?.id !== user?.id) {
      dispatch(AccessDeviceActions.fetchAccessDeviceByUserId(user?.id));
    }
  }, [id, user, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Run on outdated data
  useEffect(() => {
    if (reload && id) {
      dispatch(AccessDeviceActions.fetchAccessDeviceById(id));
    }
  }, [id, reload, dispatch]);

  useEffect(() => {
    if (reload && user) {
      dispatch(AccessDeviceActions.fetchAccessDeviceByUserId(user?.id));
    }
  }, [user, reload, dispatch]);

  const hasAccessDevice = accessDevice?.id;
  const isAssigned = accessDevice?.user?.id;
  const isNFCView = view === 'nfc';
  const isCustomerView = view === 'customer';

  const handleUnassign = () => {
    dispatch(
      AccessDeviceActions.unassignAccessDevice({
        access_device_id: accessDevice?.id,
        user_id: accessDevice?.user?.id,
      })
    );
  };

  const handleArchiveAccessDevice = () => {
    if (accessDevice?.id) {
      dispatch(AccessDeviceActions.deleteAccessDevice(accessDevice?.id));
    }
  };

  const [toggleUnassign, UnassignConfirm] = useConfirm({
    message: i18n.t('nfc.unassign_confirm'),
    title: i18n.t('nfc.unassign_nfc_card'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.confirm'),
    onConfirm: handleUnassign,
    additionalProps: { modalProps: { maxWidth: 700 } },
  });

  const [toggleArchive, ArchiveConfirm] = useConfirm({
    message: i18n.t('nfc.archive_description_nfc_card'),
    title: i18n.t('nfc.archive_nfc_card'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.confirm'),
    onConfirm: handleArchiveAccessDevice,
    additionalProps: { modalProps: { maxWidth: 700 } },
  });

  const items = [
    ...(hasAccessDevice && isNFCView
      ? [
          ...(isAssigned
            ? [
                {
                  title: i18n.t('nfc.widget_unassign_nfc'),
                  can: { name: 'access_device', action: 'update' },
                  onClick: () => toggleUnassign(),
                },
              ]
            : [
                {
                  title: i18n.t('nfc.widget_assign_nfc'),
                  can: { name: 'access_device', action: 'update' },
                  onClick: () => setOpenAssignNFCModal(true),
                },
              ]),
          {
            title: i18n.t('nfc.edit_info'),
            can: { name: 'access_device', action: 'update' },
            onClick: () => {
              setOpenCreateNFCModal(true);
            },
          },
          {
            title: i18n.t('nfc.delete_info'),
            can: { name: 'access_device', action: 'update' },
            onClick: () => toggleArchive(),
          },
        ]
      : []),

    ...(isCustomerView
      ? [
          ...(isAssigned
            ? [
                {
                  title: i18n.t('nfc.widget_unassign_nfc'),
                  can: { name: 'access_device', action: 'update' },
                  onClick: () => toggleUnassign(),
                },
              ]
            : [
                {
                  title: i18n.t('nfc.widget_assign_nfc'),
                  can: { name: 'access_device', action: 'update' },
                  onClick: () => {
                    setOpenAssignToUserNFCModal(true);
                  },
                },
              ]),
        ]
      : []),
  ];

  const close =
    updatingAccessDevice === FETCH_STATE.DONE ||
    assigningAccessDevice === FETCH_STATE.DONE ||
    unassigningAccessDevice === FETCH_STATE.DONE;

  useEffect(() => {
    setOpenCreateNFCModal(false);
    setOpenAssignNFCModal(false);
    setOpenAssignToUserNFCModal(false);
  }, [close]);

  return (
    <Card
      menuItems={items}
      headerActionType="vertical"
      data-cy="nfc-widget-card"
      title={i18n.t('nfc.nfc')}
      contentRootClasses={cardContentClasses}
    >
      <UnassignConfirm />
      <ArchiveConfirm />
      <AssignToUserNFCCardModal
        user={user}
        open={openAssignToUserNFCModal}
        handleClick={() => {
          setOpenAssignToUserNFCModal(false);
        }}
      />
      <AssignNFCCardModal
        id={accessDevice?.id}
        open={openAssignNFCModal}
        handleClick={() => {
          setOpenAssignNFCModal(false);
        }}
      />
      {isNFCView ? (
        <CreateNFCCardModal
          id={accessDevice?.id}
          open={openCreateNFCModal}
          handleClick={() => {
            setOpenCreateNFCModal(false);
          }}
        />
      ) : null}
      {loading ? (
        <Box
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
          }}
        >
          <Loading />
        </Box>
      ) : (
        <Box>
          <CollapseCard
            title={i18n.t('nfc.widget_title')}
            dataCy={'collapse-nfc-info'}
            customClasses={collapseCardClasses}
            opened={!isCustomerView}
          >
            {accessDevice?.id && (
              <List>
                <ListItem>
                  <StyledListItemText
                    primary={`${i18n.t('nfc.card_name')}:`}
                    secondary={accessDevice?.name}
                  />
                </ListItem>
                {accessDevice?.external_type && (
                  <ListItem>
                    <StyledListItemText
                      primary={`${i18n.t('nfc.type')}:`}
                      secondary={accessDevice?.external_type}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <StyledListItemText
                    primary={`${i18n.t('nfc.serial')}:`}
                    secondary={accessDevice?.device_id}
                  />
                </ListItem>
                <ListItem>
                  <StyledListItemText
                    primary={`${i18n.t('common.status')}:`}
                    secondary={
                      <ItemStatus
                        variant="labelValue"
                        i18nKey="nfc.status"
                        status={accessDevice?.status}
                      />
                    }
                  />
                </ListItem>
                {accessDevice?.brokers?.length > 0 && (
                  <ListItem>
                    <StyledListItemText
                      primary={`${i18n.t('nfc.broker')}:`}
                      secondary={
                        accessDevice?.brokers[0].name.charAt(0).toUpperCase() +
                        accessDevice?.brokers[0].name.slice(1).toLowerCase()
                      }
                    />
                  </ListItem>
                )}
              </List>
            )}
          </CollapseCard>
          {!accessDevice?.id && (
            <Box style={{ margin: '24px 24px 8px 16px' }}>
              <Box component="span">
                <Typography color="textPrimary" variant="body2">
                  {i18n.t('nfc.no_nfc')}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Card>
  );
};

NFCDetailsWidget.propTypes = {
  id: PropTypes.number,
  user: PropTypes.object,
  view: PropTypes.string,
};

export default NFCDetailsWidget;
